import { useCallback } from 'react';
import { message } from 'antd';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { ErrorCode } from 'global/const';
import { useConfigContext, useStaffContext } from 'store';
import { v4 as uuid } from 'uuid';
import moment from 'moment';
import apiMethod from './method';
import showLoading from './loading';

const useApi = (apiName) => {
    const { apiUrl, fileApiUrl } = useConfigContext();
    const { idToken } = useStaffContext();

    const request = useCallback(async (args = {}) => {
        const {
            path,
            query,
            serializer,
            body,
            headers,
            responseType,
            loading = true,
            handleError = true,
            errorMsg = true
        } = args;

        // 檢查 JWT 是否已過期，如果過期了就讓他重新登入
        let newToken = idToken;
        if (idToken) {
            const decode = jwtDecode(idToken);
            const exp = decode.exp * 1000 - Date.now();
            // 過期時間是否小於一分鐘或已經過期了，重新整理
            if (exp < 60 * 1000) {
                alert('Token 已過期，請重新登入');
                window.location.reload();
                return;
            }
        }

        if (loading) {
            showLoading(true);
        }

        try {
            const api = apiMethod[apiName];
            const {
                method,
                url,
                headers: defaultHeaders
            } = typeof api === 'function' ? api(path) : api;

            const requestConfig = {
                headers: {
                    ...defaultHeaders,
                    ...headers,
                    Authorization: `Bearer ${newToken}`,
                    'X-From-Channel': 'rentcar-backstage',
                    'X-Client-TraceId': uuid(),
                    'X-Client-Date': moment().format('YYYY-MM-DDTHH:mm:ss.SSSZ')
                },
                method,
                baseURL: url.startsWith('government/') ? fileApiUrl : apiUrl,
                url,
                params: query,
                paramsSerializer: serializer,
                data: body,
                responseType
            };

            const res = await axios(requestConfig);
            if (loading) {
                showLoading(false);
            }
            return res.data;
        } catch (err) {
            if (loading) {
                showLoading(false);
            }
            if (handleError) {
                const errorStr = Object.values(err.response?.data.errors || {});
                const errorKey = Object.keys(ErrorCode).find((key) => errorStr.includes(key));
                const msg = ErrorCode[errorKey] ?? err.response?.data.title ?? err.message;
                if (errorMsg) {
                    message.error(msg);
                }
                return { error: msg };
            }
            return { error: err.response?.data.errors };
        }
    }, [apiName, apiUrl, fileApiUrl, idToken]);

    return request;
};

export default useApi;
